// <s-sheet-reviews>: renders product reviews from a published Google Sheet CSV, client-side.
// The Sheet is the source of truth. Rows show only when Publish is on (or the Sheet has no Publish
// column) and the rating is a whole 1-5 value. Alternate headings (Stars, Review text, Customer name,
// Verified buyer, Training type, Photo URL, ...) are recognised so older Sheets keep working.
// Markup comes from the Liquid template: a <template data-card-template> for cards, and
// [data-state] panels for loading / empty / error / content. Copy arrives in data-i18n as JSON.
class SSheetReviews extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.url = (this.dataset.csvUrl || '').trim();
    this.initialCount = parseInt(this.dataset.initialCount || '6', 10) || 6;
    // Reviews added per "Load more" tap (data-page-size), defaulting to the initial count.
    this.pageSize = parseInt(this.dataset.pageSize || '', 10) || this.initialCount;
    try {
      this.i18n = JSON.parse(this.dataset.i18n || '{}');
    } catch {
      this.i18n = {};
    }

    this.grid = this.querySelector('[data-grid]');
    this.template = this.querySelector('template[data-card-template]');
    this.loadMoreButton = this.querySelector('[data-load-more]');
    this.showingNode = this.querySelector('[data-showing]');

    this.loadMoreButton?.addEventListener('click', () => this.showMore());
    this.querySelector('[data-retry]')?.addEventListener('click', () => this.load());

    // Filters, sort and search. All optional: the component works without the toolbar markup.
    this.filters = { rating: 'all', photos: false, role: '', query: '' };
    this.sort = 'featured';
    this.noMatches = this.querySelector('[data-no-matches]');
    this.querySelectorAll('[data-filter-rating]').forEach((button) => {
      button.addEventListener('click', () => {
        const value = button.dataset.filterRating;
        const next = value === 'all' || value === String(this.filters.rating) ? 'all' : parseInt(value, 10);
        this.update({ rating: next });
      });
    });
    this.querySelector('[data-filter-photos]')?.addEventListener('click', () => this.update({ photos: !this.filters.photos }));
    this.querySelector('[data-filter-role]')?.addEventListener('change', (event) => this.update({ role: event.target.value }));
    this.querySelector('[data-sort]')?.addEventListener('change', (event) => {
      this.sort = event.target.value;
      this.applyFilters(); // same results, new order: no scroll needed
    });
    let searchTimer;
    this.querySelector('[data-search]')?.addEventListener('input', (event) => {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(() => this.update({ query: event.target.value.trim().toLowerCase() }), 200);
    });
    this.querySelectorAll('[data-clear-filters]').forEach((button) => button.addEventListener('click', () => this.clearFilters()));

    this.load();
  }

  t(key, values = {}) {
    let text = this.i18n[key] || '';
    for (const [name, value] of Object.entries(values)) {
      text = text.split(`[${name}]`).join(String(value));
    }
    return text;
  }

  setState(state) {
    this.querySelectorAll('[data-state]').forEach((panel) => {
      panel.hidden = panel.dataset.state !== state;
    });
    this.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
  }

  // Loading strategy (the Google feed is slow, often 1-3 s on phones):
  // 1. Show the last copy saved in this browser, or else the copy of the Sheet built into the page
  //    (snippets/reviews-snapshot.liquid), immediately. No waiting on Google for the first paint.
  // 2. Use the request the page started early (see snippets/reviews-feed-prefetch.liquid), or fetch now.
  // 3. When fresh data arrives, save it and re-render only if something changed.
  async load() {
    if (!this.url) {
      this.setState('error');
      return;
    }
    const shown = this.readCache() || this.readSnapshot();
    if (shown) {
      this.render(shown);
    } else {
      this.setState('loading');
    }
    try {
      const reviews = SSheetReviews.rowsToReviews(SSheetReviews.parseCSV(await this.fetchFeed()));
      this.writeCache(reviews);
      if (!shown || JSON.stringify(shown) !== JSON.stringify(reviews)) this.render(reviews);
    } catch {
      if (!shown) this.setState('error');
    }
  }

  readSnapshot() {
    try {
      const csv = this.querySelector('script[data-reviews-snapshot]')?.textContent.trim();
      if (!csv) return null;
      const reviews = SSheetReviews.rowsToReviews(SSheetReviews.parseCSV(csv));
      return reviews.length ? reviews : null;
    } catch {
      return null;
    }
  }

  fetchFeed() {
    const early = window.ArmLabzReviewFeed?.[this.url];
    if (early) {
      delete window.ArmLabzReviewFeed[this.url]; // one use; a retry fetches fresh
      return early;
    }
    return fetch(this.url).then((response) => {
      if (!response.ok) throw new Error('Review feed request failed');
      return response.text();
    });
  }

  get cacheKey() {
    return `armlabz-reviews:${this.url}`;
  }

  readCache() {
    try {
      const saved = JSON.parse(localStorage.getItem(this.cacheKey) || 'null');
      // Anything older than 7 days is ignored rather than shown.
      if (!saved || !Array.isArray(saved.reviews) || Date.now() - saved.savedAt > 7 * 864e5) return null;
      return saved.reviews;
    } catch {
      return null;
    }
  }

  writeCache(reviews) {
    try {
      localStorage.setItem(this.cacheKey, JSON.stringify({ savedAt: Date.now(), reviews }));
    } catch {
      // Storage full or blocked: the page still works, it just won't be instant next time.
    }
  }

  render(reviews) {
    if (!reviews.length) {
      this.all = null;
      this.setState('empty');
      return;
    }
    this.all = reviews.map((review, order) => ({ ...review, order, time: SSheetReviews.dateValue(review.date) }));
    this.renderSummary();
    if (!this.grid || !this.template) {
      this.setState('content'); // summary-only mode (homepage testimonials)
      return;
    }
    this.renderFilterCounts();
    this.setState('content');
    this.applyFilters();
  }

  // ---- CSV parsing (quote-aware, handles CRLF, escaped quotes and a BOM header) ----

  static parseCSV(text) {
    const rows = [];
    let row = [];
    let field = '';
    let inQuotes = false;
    const pushRow = () => {
      row.push(field);
      field = '';
      if (row.some((cell) => String(cell).trim() !== '')) rows.push(row);
      row = [];
    };

    for (let i = 0; i < text.length; i += 1) {
      const char = text[i];
      const next = text[i + 1];
      if (char === '"') {
        if (inQuotes && next === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        row.push(field);
        field = '';
      } else if ((char === '\n' || char === '\r') && !inQuotes) {
        if (char === '\r' && next === '\n') i += 1;
        pushRow();
      } else {
        field += char;
      }
    }
    pushRow();
    return rows;
  }

  static normalizeHeader(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/^\uFEFF/, '')
      .replace(/[^a-z0-9]/g, '');
  }

  static truthy(value) {
    return ['yes', 'y', 'true', '1', 'verified', 'featured', 'publish', 'published'].includes(
      String(value || '').trim().toLowerCase()
    );
  }

  static safeImageUrl(value) {
    const candidate = String(value || '').trim();
    return /^https?:\/\//i.test(candidate) ? candidate : '';
  }

  static rowsToReviews(rows) {
    if (!rows.length) return [];
    const index = {};
    rows[0].map(SSheetReviews.normalizeHeader).forEach((header, position) => {
      if (header) index[header] = position;
    });
    const get = (row, names) => {
      for (const name of names) {
        const key = SSheetReviews.normalizeHeader(name);
        if (key in index) return String(row[index[key]] || '').trim();
      }
      return '';
    };
    const hasPublishColumn = 'publish' in index || 'published' in index || 'show' in index;

    return rows
      .slice(1)
      .map((row) => ({
        publish: hasPublishColumn ? SSheetReviews.truthy(get(row, ['Publish', 'Published', 'Show'])) : true,
        name: get(row, ['Name', 'Customer', 'Customer name']),
        rating: parseInt(get(row, ['Rating', 'Stars', 'Star rating']), 10),
        title: get(row, ['Title', 'Review title']),
        review: get(row, ['Review', 'Review text', 'Body', 'Comment']),
        date: get(row, ['Date', 'Review date']),
        verified: SSheetReviews.truthy(get(row, ['Verified', 'Verified buyer'])),
        role: get(row, ['Role', 'Customer role', 'Training type']),
        featured: SSheetReviews.truthy(get(row, ['Featured', 'Feature'])),
        image: SSheetReviews.safeImageUrl(get(row, ['Image URL', 'Image', 'Photo URL'])),
      }))
      .filter((review) => review.publish && review.rating >= 1 && review.rating <= 5);
  }

  // ---- Filtering and sorting ----

  static dateValue(text) {
    const value = String(text || '').replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
    if (!value) return 0;
    let time = Date.parse(value);
    if (Number.isNaN(time)) time = Date.parse(`1 ${value}`); // "April 2026"
    return Number.isNaN(time) ? 0 : time;
  }

  clearFilters() {
    const search = this.querySelector('[data-search]');
    if (search) search.value = '';
    const role = this.querySelector('[data-filter-role]');
    if (role) role.value = '';
    this.update({ rating: 'all', photos: false, role: '', query: '' });
  }

  update(changes) {
    Object.assign(this.filters, changes);
    this.applyFilters();
    this.keepToolbarInView();
  }

  // When results shrink, the page gets shorter and the shopper can end up staring at the FAQ.
  // Pull the filter bar back to the top of the screen only if it has scrolled out of view.
  keepToolbarInView() {
    const toolbar = this.querySelector('[data-toolbar]');
    if (!toolbar) return;
    const top = toolbar.getBoundingClientRect().top;
    if (top >= 0 && top < window.innerHeight * 0.6) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    toolbar.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  }

  applyFilters(moveFocus = false) {
    if (!this.all) return;
    const { rating, photos, role, query } = this.filters;
    const list = this.all.filter((review) => {
      if (rating !== 'all' && review.rating !== rating) return false;
      if (photos && !review.image) return false;
      if (role && review.role !== role) return false;
      if (query && !`${review.title} ${review.review} ${review.name} ${review.role}`.toLowerCase().includes(query)) return false;
      return true;
    });

    const bySheetOrder = (a, b) => a.order - b.order;
    const sorters = {
      featured: (a, b) => Number(b.featured) - Number(a.featured) || bySheetOrder(a, b),
      newest: (a, b) => b.time - a.time || bySheetOrder(a, b),
      highest: (a, b) => b.rating - a.rating || Number(b.featured) - Number(a.featured) || bySheetOrder(a, b),
      lowest: (a, b) => a.rating - b.rating || bySheetOrder(a, b),
      photos: (a, b) => Number(Boolean(b.image)) - Number(Boolean(a.image)) || Number(b.featured) - Number(a.featured) || bySheetOrder(a, b),
    };
    list.sort(sorters[this.sort] || sorters.featured);

    this.reviews = list;
    this.visible = 0;
    this.grid.replaceChildren();

    // Reflect state on the controls.
    this.querySelectorAll('[data-filter-rating]').forEach((button) => {
      const value = button.dataset.filterRating;
      const pressed = value === 'all' ? rating === 'all' : String(rating) === value;
      button.setAttribute('aria-pressed', String(pressed));
    });
    this.querySelector('[data-filter-photos]')?.setAttribute('aria-pressed', String(photos));
    const filtered = rating !== 'all' || photos || role || query;
    this.querySelectorAll('[data-clear-filters]').forEach((button) => {
      if (button.closest('[data-no-matches]')) return;
      button.hidden = !filtered;
    });

    if (this.noMatches) this.noMatches.hidden = list.length > 0;
    this.grid.hidden = list.length === 0;
    if (!list.length) {
      if (this.loadMoreButton) this.loadMoreButton.hidden = true;
      if (this.showingNode) this.showingNode.textContent = this.t('no_matches');
      return;
    }
    this.showMore(moveFocus);
  }

  renderFilterCounts() {
    const counts = { all: this.all.length };
    for (let stars = 1; stars <= 5; stars += 1) counts[stars] = this.all.filter((review) => review.rating === stars).length;
    this.querySelectorAll('[data-filter-rating]').forEach((button) => {
      const value = button.dataset.filterRating;
      const count = counts[value] ?? 0;
      const countNode = button.querySelector('[data-filter-count]');
      if (countNode) countNode.textContent = String(count);
      // Every star filter (5 to 1) is always shown with its count, even at 0, so shoppers can check
      // for low ratings themselves. Choosing an empty one shows the "no reviews match" message.
    });

    const photoCount = this.all.filter((review) => review.image).length;
    const photosButton = this.querySelector('[data-filter-photos]');
    if (photosButton) {
      photosButton.hidden = photoCount === 0;
      const countNode = photosButton.querySelector('[data-filter-count]');
      if (countNode) countNode.textContent = String(photoCount);
    }

    const roleSelect = this.querySelector('[data-filter-role]');
    if (roleSelect) {
      const roles = [...new Set(this.all.map((review) => review.role).filter(Boolean))].sort((a, b) => a.localeCompare(b));
      // Rebuild (a refresh must not duplicate options) and keep the shopper's choice if it still exists.
      const current = this.filters.role;
      roleSelect.querySelectorAll('option:not([value=""])').forEach((option) => option.remove());
      roles.forEach((role) => roleSelect.append(new Option(role, role)));
      if (!roles.includes(current)) this.filters.role = '';
      roleSelect.value = this.filters.role;
      // Only useful as a short list: hidden when there's one type or a long tail of free-text values.
      const wrapper = roleSelect.closest('[data-role-wrapper]') || roleSelect;
      wrapper.hidden = roles.length < 2 || roles.length > 12;
    }

    // "Newest" only makes sense when the dates actually differ.
    const sortSelect = this.querySelector('[data-sort]');
    const newest = sortSelect?.querySelector('option[value="newest"]');
    if (newest) {
      const distinctDates = new Set(this.all.map((review) => review.time).filter(Boolean)).size;
      newest.hidden = distinctDates < 2;
      newest.disabled = distinctDates < 2;
      if (newest.disabled && this.sort === 'newest') {
        this.sort = 'featured';
        sortSelect.value = 'featured';
      }
    }
  }

  // ---- Rendering ----

  renderSummary() {
    const reviews = this.all;
    const total = reviews.length;
    const average = reviews.reduce((sum, review) => sum + review.rating, 0) / total;

    const averageNode = this.querySelector('[data-average]');
    if (averageNode) averageNode.textContent = average.toFixed(1);
    const averageStars = this.querySelector('[data-average-stars]');
    if (averageStars) averageStars.setAttribute('aria-label', this.t('stars_label', { rating: average.toFixed(1) }));
    const averageFill = this.querySelector('[data-average-fill]');
    if (averageFill) averageFill.style.width = `${(average / 5) * 100}%`;
    const countNode = this.querySelector('[data-count]');
    if (countNode) countNode.textContent = this.t(total === 1 ? 'count_one' : 'count_other', { count: total });

    // Page-level summaries outside this element (e.g. under the product title) mirror the same numbers.
    document.querySelectorAll('[data-reviews-summary]').forEach((summary) => {
      const text = summary.querySelector('[data-reviews-summary-text]');
      if (text) {
        text.textContent = this.t(total === 1 ? 'summary_one' : 'summary_other', {
          rating: average.toFixed(1),
          count: total,
        });
      }
      const fill = summary.querySelector('[data-reviews-summary-fill]');
      if (fill) fill.style.width = `${(average / 5) * 100}%`;
      summary.hidden = false;
    });

    for (let stars = 1; stars <= 5; stars += 1) {
      const count = reviews.filter((review) => review.rating === stars).length;
      const percent = Math.round((count / total) * 100);
      const bar = this.querySelector(`[data-bar="${stars}"]`);
      if (bar) bar.style.width = `${percent}%`;
      const percentNode = this.querySelector(`[data-percent="${stars}"]`);
      if (percentNode) percentNode.textContent = `${percent}%`;
      const row = this.querySelector(`[data-breakdown-row="${stars}"]`);
      if (row) {
        row.setAttribute(
          'aria-label',
          `${this.t('breakdown_row', { stars })}: ${this.t('breakdown_value', { percent })}. ${this.t('filter_by', { stars })}`
        );
      }
    }
  }

  showMore(moveFocus = true) {
    if (!this.reviews) return;
    const start = this.visible;
    const step = start === 0 ? this.initialCount : this.pageSize;
    const end = Math.min(start + step, this.reviews.length);
    const fragment = document.createDocumentFragment();
    let firstNew = null;

    for (let i = start; i < end; i += 1) {
      const card = this.createCard(this.reviews[i]);
      if (!firstNew) firstNew = card.querySelector('article');
      fragment.append(card);
    }
    this.grid.append(fragment);
    this.visible = end;

    const done = this.visible >= this.reviews.length;
    if (this.loadMoreButton) this.loadMoreButton.hidden = done;
    if (this.showingNode) {
      this.showingNode.textContent = this.t('showing', { shown: this.visible, total: this.reviews.length });
    }
    if (moveFocus && firstNew) firstNew.focus({ preventScroll: false });
  }

  createCard(review) {
    const node = this.template.content.firstElementChild.cloneNode(true);
    const slot = (name) => node.querySelector(`[data-slot="${name}"]`);
    const setText = (name, value) => {
      const element = slot(name);
      if (!element) return;
      if (value) {
        element.textContent = value;
      } else {
        element.remove();
      }
    };
    const name = review.name || this.t('anonymous');

    const media = slot('media');
    const image = slot('image');
    if (review.image && media && image) {
      image.src = review.image;
      image.alt = this.t('photo_alt', { name });
      image.addEventListener('error', () => media.remove(), { once: true });
      media.hidden = false;
    } else {
      media?.remove();
    }

    const stars = slot('stars');
    if (stars) stars.setAttribute('aria-label', this.t('stars_label', { rating: review.rating }));
    const starsFill = slot('stars-fill');
    if (starsFill) starsFill.style.width = `${review.rating * 20}%`;

    if (!review.featured) slot('featured')?.remove();
    if (!review.verified) slot('verified')?.remove();
    const badges = slot('badges');
    if (badges && !badges.children.length) badges.remove();

    setText('title', review.title);
    setText('review', review.review);
    setText('name', name);
    setText('role', review.role);
    setText('date', review.date);

    return node;
  }
}

if (!customElements.get('s-sheet-reviews')) customElements.define('s-sheet-reviews', SSheetReviews);
